/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * HyusnSu Sim (hssim@rsupport.com)
 *
 * @description
 *
 * @created at Thu Mar 23 2023
 **/

import cx from "classnames";
import { useTheme } from "@emotion/react";
import { checkboxInputStyle, customCheckBoxStyle, labelStyle, hoverStyle, labelTextStyle } from "./private/emotion";
import { ForwardedRef, forwardRef } from "react";

export interface CheckboxProps extends React.InputHTMLAttributes<HTMLInputElement> {
	className?: string;
	hideLabel?: boolean;
	labelPosition?: DesignStuff.Checkbox.LabelPosition;
}

/**
 * @param hideLabel label 숨김처리 여부
 * @param labelPosition label 위치 [ "left" | "right" ]
 */

const Checkbox = forwardRef(
	(
		{ className = "", hideLabel = false, labelPosition = "right", children, ...rest }: CheckboxProps,
		ref: ForwardedRef<HTMLInputElement>
	) => {
		const theme = useTheme();

		const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			e.stopPropagation();
			rest.onChange && rest.onChange(e);
		};

		return (
			<div className={cx(className, { checked: rest.checked })} data-type="checkbox">
				<label htmlFor={rest.name} css={labelStyle({ labelPosition })}>
					<input
						{...rest}
						ref={ref}
						css={checkboxInputStyle}
						className="hideCheckBox"
						type="checkbox"
						onChange={handleChange}
					/>
					<div css={hoverStyle(theme, rest.disabled, hideLabel, labelPosition)}>
						<div
							css={customCheckBoxStyle(theme, {
								isChecked: (rest.checked && rest.checked) || false,
								disabled: (rest.disabled && rest.disabled) || false
							})}
						/>
					</div>
					<div css={labelTextStyle(hideLabel)}>{children}</div>
				</label>
			</div>
		);
	}
);

export default Checkbox;
